import React, { useEffect, useState, useMemo } from 'react';
import { checkHealth, getQueueStatus } from '../../api/health';
import { listWorkloads, getPortainerHealth } from '../../api/portainerService';
import { useLoader } from '../../context/LoaderContext';

const endpoints = [
    { name: "Redis", path: "api/v1/HealthCheck/RedisMonitoring", icon: "ri-database-2-line" },
    { name: "Monitoring", path: "api/v1/HealthCheck/AllMonitoring", icon: "ri-dashboard-line" },
    { name: "Kelime Dağarcığı", path: "api/v1/HealthCheck/KelimeDagarcigi", icon: "ri-book-read-line" },
    { name: "Harf Seç", path: "api/v1/HealthCheck/HarfSec", icon: "ri-font-color" },
    { name: "Zincir", path: "api/v1/HealthCheck/Zincir", icon: "ri-links-line" },
    { name: "Word Matches", path: "api/v1/HealthCheck/WordMatches", icon: "ri-question-answer-line" },
    { name: "Four x Four", path: "api/v1/HealthCheck/FourxFour", icon: "ri-grid-fill" },
    { name: "Dictionary", path: "api/v1/HealthCheck/Dictionary", icon: "ri-book-2-line" },
];

const DashBoard = () => {
    const { showLoader, hideLoader } = useLoader();
    const [statuses, setStatuses] = useState({});
    const [queueData, setQueueData] = useState([]);
    const [workloadData, setWorkloadData] = useState([]);
    const [portainerHealth, setPortainerHealth] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchAllStatuses = async () => {
        showLoader();
        setLoading(true);

        const healthPromises = endpoints.map(async (endpoint) => {
            try {
                setStatuses(prev => ({ ...prev, [endpoint.path]: { status: 'loading' } }));
                const res = await checkHealth(endpoint.path);
                setStatuses(prev => ({
                    ...prev,
                    [endpoint.path]: { status: 'success', data: res }
                }));
            } catch (err) {
                setStatuses(prev => ({
                    ...prev,
                    [endpoint.path]: { status: 'error', error: err }
                }));
            }
        });

        const fetchSecondaryData = async () => {
            getQueueStatus().then(res => setQueueData(res || [])).catch(() => setQueueData([]));
            listWorkloads().then(res => setWorkloadData(res?.data || [])).catch(() => setWorkloadData([]));
            getPortainerHealth().then(res => setPortainerHealth(res || { status: 'success' })).catch(() => setPortainerHealth({ status: 'error' }));
        };

        await Promise.all(healthPromises);
        fetchSecondaryData();
        setLoading(false);
        hideLoader();
    };

    useEffect(() => {
        fetchAllStatuses();
    }, []);

    const stats = useMemo(() => {
        const totalHealth = endpoints.length;
        const healthyOnes = Object.values(statuses).filter(s => s.status === 'success').length;
        const totalServices = workloadData.filter(i => i.type === 'service').length;
        const totalContainers = workloadData.filter(i => i.type === 'container' && i.state === 'running').length;
        const totalQueue = queueData.reduce((acc, curr) => acc + (curr.messagesReady || 0), 0);

        return { healthyOnes, totalHealth, totalServices, totalContainers, totalQueue };
    }, [statuses, workloadData, queueData]);

    return (
        <div className="container-fluid p-0">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-20">
                <div>
                    <h5 className="fw-bold mb-0">Sistem İzleme Merkezi</h5>
                    <p className="text-secondary-light text-sm mb-0">Operasyonel durum özeti ve canlı metrikler</p>
                </div>
                <div className="d-flex gap-2">
                    <button onClick={fetchAllStatuses} className="btn btn-outline-primary shadow-none btn-sm radius-8 px-16 h-40-px d-flex align-items-center gap-2" disabled={loading}>
                        <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
                        {loading ? 'Güncelleniyor' : 'Yenile'}
                    </button>
                    <div className={`d-flex align-items-center gap-2 px-12 radius-8 border ${portainerHealth?.status === 'error' ? 'bg-danger-focus border-danger-100 text-danger-main' : 'bg-success-focus border-success-100 text-success-main'}`}>
                        <div className={`w-8-px h-8-px radius-circle ${portainerHealth?.status === 'error' ? 'bg-danger-main' : 'bg-success-main animate-pulse'}`}></div>
                        <span className="text-xs fw-bold">Docker Online</span>
                    </div>
                </div>
            </div>

            {/* Metrics */}
            <div className="row gy-3 mb-24">
                <div className="col-md-3">
                    <div className="card radius-12 border-0 shadow-sm overflow-hidden bg-primary-600 text-white">
                        <div className="card-body p-20 position-relative">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <p className="text-white text-opacity-75 text-xs fw-bold uppercase mb-4">Sistem Sağlığı</p>
                                    <h3 className="mb-0 fw-bold">{stats.healthyOnes}/{stats.totalHealth}</h3>
                                </div>
                                <div className="p-10 bg-white bg-opacity-20 radius-8"><i className="ri-heart-pulse-line text-xl"></i></div>
                            </div>
                            <div className="mt-12 text-xs text-white text-opacity-75">Tüm Endpoint Durum Özeti</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card radius-12 border-0 shadow-sm bg-white">
                        <div className="card-body p-20">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <p className="text-secondary-light text-xs fw-bold uppercase mb-4">Aktif Servisler</p>
                                    <h3 className="mb-0 fw-bold">{stats.totalServices}</h3>
                                </div>
                                <div className="p-10 bg-success-focus text-success-main radius-8"><i className="ri-server-line text-xl"></i></div>
                            </div>
                            <div className="mt-12 text-xs text-secondary-light"><span className="text-success-main fw-bold">{stats.totalContainers}</span> çalışan container</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card radius-12 border-0 shadow-sm bg-white">
                        <div className="card-body p-20">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <p className="text-secondary-light text-xs fw-bold uppercase mb-4">Kuyruk Durumu</p>
                                    <h3 className="mb-0 fw-bold">{stats.totalQueue}</h3>
                                </div>
                                <div className="p-10 bg-info-focus text-info-main radius-8"><i className="ri-list-ordered text-xl"></i></div>
                            </div>
                            <div className="mt-12 text-xs text-secondary-light">
                                {stats.totalQueue > 0
                                    ? `${queueData.filter(q => (q.messagesReady || 0) > 0).length} kuyrukta birikme mevcut`
                                    : 'Birikme yok'}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card radius-12 border-0 shadow-sm bg-white">
                        <div className="card-body p-20">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <p className="text-secondary-light text-xs fw-bold uppercase mb-4">Docker Statu</p>
                                    <h3 className="mb-0 fw-bold">{portainerHealth?.status === 'error' ? 'Bağlantı Yok' : 'Online'}</h3>
                                </div>
                                <div className={`p-10 ${portainerHealth?.status === 'error' ? 'bg-danger-focus text-danger-main' : 'bg-success-focus text-success-main'} radius-8`}>
                                    <i className="ri-instance-line text-xl"></i>
                                </div>
                            </div>
                            <div className="mt-12 text-xs text-secondary-light">API entegrasyonu aktif</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Unified Status Panel */}
            <div className="card radius-12 border-0 shadow-sm bg-white overflow-hidden">
                <div className="row g-0">
                    {/* Docker */}
                    <div className="col-md-4 border-end border-neutral-100">
                        <div className="px-16 py-10 bg-neutral-50 border-bottom border-neutral-100 d-flex align-items-center justify-content-between">
                            <span className="text-xxs fw-bold text-secondary-light uppercase">Docker</span>
                            <span className="text-xxs text-neutral-400">{stats.totalServices} servis · {stats.totalContainers} container</span>
                        </div>
                        <div className="overflow-auto custom-scrollbar" style={{ maxHeight: '360px' }}>
                            {workloadData.filter(i => i.type === 'service').map((service, index) => {
                                const running = (service.replicaRunning || 0) === (service.replicaDesired || 0);
                                const desc = service.description || service.desc;
                                return (
                                    <div key={`svc-${index}`} className="px-16 py-8 border-bottom border-neutral-100 hover-bg-neutral-50 transition-1">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-2 text-truncate">
                                                <div className={`w-8-px h-8-px radius-circle flex-shrink-0 ${running ? 'bg-success-main shadow-success' : 'bg-warning-main'}`}></div>
                                                <span className="text-xs fw-bold text-dark text-truncate" title={service.name}>{service.name}</span>
                                            </div>
                                            <span className="text-xxs text-neutral-400 flex-shrink-0">{service.replicaRunning}/{service.replicaDesired}</span>
                                        </div>
                                        {desc && <div className="text-xxs text-neutral-400 mt-2 ps-16 text-truncate" title={desc}>{desc}</div>}
                                    </div>
                                );
                            })}
                            {workloadData.filter(i => i.type === 'container' && !i.serviceId).map((c, index) => {
                                const desc = c.description || c.desc;
                                return (
                                    <div key={`ctr-${index}`} className="px-16 py-8 border-bottom border-neutral-100 hover-bg-neutral-50 transition-1">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-2 text-truncate">
                                                <div className={`w-8-px h-8-px radius-circle flex-shrink-0 ${c.state === 'running' ? 'bg-success-main shadow-success' : 'bg-danger-main shadow-danger'}`}></div>
                                                <span className="text-xs fw-bold text-dark text-truncate" title={c.name}>{c.name}</span>
                                            </div>
                                            <span className="text-xxs text-neutral-400 flex-shrink-0 uppercase">{c.state}</span>
                                        </div>
                                        {desc && <div className="text-xxs text-neutral-400 mt-2 ps-16 text-truncate" title={desc}>{desc}</div>}
                                    </div>
                                );
                            })}
                            {workloadData.length === 0 && (
                                <div className="text-center py-20"><p className="text-xxs text-neutral-400 mb-0">Kayıt bulunamadı</p></div>
                            )}
                        </div>
                    </div>

                    {/* Endpoints */}
                    <div className="col-md-4 border-end border-neutral-100">
                        <div className="px-16 py-10 bg-neutral-50 border-bottom border-neutral-100 d-flex align-items-center justify-content-between">
                            <span className="text-xxs fw-bold text-secondary-light uppercase">Endpoint</span>
                            <span className="text-xxs text-neutral-400">{stats.healthyOnes}/{stats.totalHealth} sağlıklı</span>
                        </div>
                        <div className="overflow-auto custom-scrollbar" style={{ maxHeight: '360px' }}>
                            {endpoints.map((ep, index) => {
                                const st = statuses[ep.path];
                                const isSuccess = st?.status === 'success';
                                const isLoading = st?.status === 'loading';
                                const desc = st?.data?.description || st?.data?.desc;
                                return (
                                    <div key={index} className="px-16 py-8 border-bottom border-neutral-100 hover-bg-neutral-50 transition-1">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-2 text-truncate">
                                                <i className={`${ep.icon} text-xs text-secondary-light flex-shrink-0`}></i>
                                                <span className="text-xs fw-bold text-dark text-truncate" title={ep.name}>{ep.name}</span>
                                            </div>
                                            {isLoading ? (
                                                <div className="spinner-border spinner-border-sm text-warning flex-shrink-0" style={{ width: '10px', height: '10px' }} role="status"></div>
                                            ) : (
                                                <div className={`w-8-px h-8-px radius-circle flex-shrink-0 ${isSuccess ? 'bg-success-main shadow-success' : 'bg-danger-main shadow-danger'}`}></div>
                                            )}
                                        </div>
                                        {desc && <div className="text-xxs text-neutral-400 mt-2 ps-16 text-truncate" title={desc}>{desc}</div>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Queue */}
                    <div className="col-md-4">
                        <div className="px-16 py-10 bg-neutral-50 border-bottom border-neutral-100 d-flex align-items-center justify-content-between">
                            <span className="text-xxs fw-bold text-secondary-light uppercase">Kuyruk</span>
                            <span className="text-xxs text-neutral-400">{stats.totalQueue} bekleyen</span>
                        </div>
                        <div className="overflow-auto custom-scrollbar" style={{ maxHeight: '360px' }}>
                            {queueData.map((queue, index) => {
                                const desc = queue.description || queue.desc;
                                return (
                                    <div key={index} className="px-16 py-8 border-bottom border-neutral-100 hover-bg-neutral-50 transition-1">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-2 text-truncate">
                                                <div className={`w-8-px h-8-px radius-circle flex-shrink-0 ${queue.exists ? 'bg-success-main shadow-success' : 'bg-danger-main shadow-danger'}`}></div>
                                                <span className="text-xs fw-bold text-dark text-truncate font-monospace" title={queue.queueName}>{queue.queueName}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-8 flex-shrink-0">
                                                <span className="text-xxs text-neutral-400">C:{queue.consumerCount}</span>
                                                <span className={`badge ${queue.messagesReady > 50 ? 'bg-danger-focus text-danger-main' : 'bg-primary-focus text-primary-600'} radius-pill px-8 py-2 h-20-px d-inline-flex align-items-center text-xxs`}>
                                                    {queue.messagesReady}
                                                </span>
                                            </div>
                                        </div>
                                        {desc && <div className="text-xxs text-neutral-400 mt-2 ps-16 text-truncate" title={desc}>{desc}</div>}
                                    </div>
                                );
                            })}
                            {queueData.length === 0 && (
                                <div className="text-center py-20"><p className="text-xxs text-neutral-400 mb-0">Aktif kuyruk bulunamadı</p></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .shadow-success { box-shadow: 0 0 10px rgba(71, 158, 71, 0.5); }
                .shadow-danger { box-shadow: 0 0 10px rgba(220, 53, 69, 0.5); }
                .hover-bg-neutral-50:hover { background-color: #f8f9fa; }
                .last-mb-0:last-child { margin-bottom: 0 !important; }
                .text-xxs { font-size: 0.65rem; }
                .text-xxxs { font-size: 0.55rem; }
                .uppercase { text-transform: uppercase; letter-spacing: 0.5px; }
                .last-border-0:last-child { border-bottom: 0 !important; }
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
            `}} />
        </div>
    );
};

export default DashBoard;